/**
 * HTML-embed helpers for arbitrary third-party embed snippets or raw
 * iframe/HTML code (Facebook posts, Twitter/X tweets, Instagram widgets,
 * TikTok videos, custom iframes, or any hand-written HTML). The admin
 * pastes the raw embed code into the editor's "Insert HTML embed" dialog;
 * the raw HTML is base64-encoded and stored as `data-html-b64` on a
 * placeholder `<div>` inside the post/page body.
 *
 * At render time (public post/page view, and live inside the editor
 * itself) `hydrateHtmlEmbeds` walks every placeholder in the DOM, decodes
 * the payload, injects it **verbatim — no sanitisation** and re-creates
 * any `<script>` elements so browsers actually execute them (React's
 * `innerHTML` intentionally does not run inline scripts).
 *
 * Trust model: this block is only ever authored by a logged-in admin
 * with publish rights (same trust level as the rest of the post body,
 * which is already stored/rendered as raw HTML). Per explicit product
 * decision the embed block preserves whatever the admin pastes exactly
 * as-is, with no tag/attribute/script allowlist — it's a deliberate
 * escape hatch for embeds (or raw HTML) that the sanitizer would
 * otherwise mangle.
 */

// UTF-8-safe base64 encode/decode. Straight `btoa` chokes on non-Latin1
// codepoints (emoji, curly quotes, non-English text) — encoding via
// TextEncoder → binary string first fixes that.
export const encodeEmbedHtml = (str) => {
  const bytes = new TextEncoder().encode(String(str ?? ""));
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

export const decodeEmbedHtml = (b64) => {
  try {
    const bin = atob(String(b64 ?? ""));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return "";
  }
};

/**
 * Walks a container and hydrates every `.html-embed-placeholder[data-html-b64]`
 * inside it. Idempotent — placeholders already hydrated (flagged with
 * `data-hydrated="true"`) are skipped, so React re-renders don't double-mount
 * an embed.
 *
 * Returns the number of embeds hydrated (useful for testing).
 */
export const hydrateHtmlEmbeds = (root) => {
  if (!root) return 0;
  const placeholders = root.querySelectorAll(
    "div.html-embed-placeholder[data-html-b64]:not([data-hydrated='true'])"
  );
  let count = 0;
  placeholders.forEach((ph) => {
    const b64 = ph.getAttribute("data-html-b64") || "";
    const raw = decodeEmbedHtml(b64);
    // Preserve the admin's HTML exactly as pasted — no sanitisation.
    ph.innerHTML = raw;
    // Replace only the inner contents — keep the placeholder wrapper so
    // subsequent re-runs can find it and skip it via data-hydrated.
    ph.setAttribute("data-hydrated", "true");
    // Re-create <script> elements so the browser executes them (React
    // `innerHTML` doesn't run scripts by design).
    reinjectScripts(ph);
    count += 1;
  });
  if (count > 0) triggerProviderReparse(root);
  return count;
};

const reinjectScripts = (container) => {
  const oldScripts = container.querySelectorAll("script");
  oldScripts.forEach((old) => {
    const fresh = document.createElement("script");
    for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
    if (old.textContent) fresh.textContent = old.textContent;
    old.parentNode.replaceChild(fresh, old);
  });
};

// After a fresh embed lands in the DOM, each provider's SDK needs a
// nudge to look for and hydrate it (their SDKs otherwise only scan
// once at initial page load).
const triggerProviderReparse = (root) => {
  try {
    if (window.FB && window.FB.XFBML && typeof window.FB.XFBML.parse === "function") {
      window.FB.XFBML.parse(root);
    }
  } catch { /* ignore SDK errors — an embed failing shouldn't take down the page */ }
  try {
    if (window.twttr && window.twttr.widgets && typeof window.twttr.widgets.load === "function") {
      window.twttr.widgets.load(root);
    }
  } catch { /* ignore */ }
  try {
    if (window.instgrm && window.instgrm.Embeds && typeof window.instgrm.Embeds.process === "function") {
      window.instgrm.Embeds.process();
    }
  } catch { /* ignore */ }
};
